import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { Check } from 'lucide-react';
import { Dialog, DialogActions } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { listRooms } from '../db/rooms';
import { moveItemsToRoom } from '../db/items';
import { iconByName } from '../rooms/roomIcons';
import '../ui/form.css';

/**
 * A room picker, for one item or for a selection.
 *
 * The room it's already in is listed and disabled rather than hidden: a list
 * that silently omits one entry reads as a bug, and seeing where it is now is
 * half of deciding where it should go.
 */
export function MoveItemsDialog({
  itemIds,
  fromRoomId,
  onClose,
  onMoved,
}: {
  itemIds: readonly string[];
  /** Where they are now. Undefined when a selection spans several rooms. */
  fromRoomId: string | undefined;
  onClose: () => void;
  onMoved: (roomId: string) => void;
}) {
  const rooms = useLiveQuery(listRooms, []);
  const [picked, setPicked] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  async function confirm() {
    if (picked === undefined) return;
    setBusy(true);
    setError(undefined);

    try {
      await moveItemsToRoom(itemIds, picked);
      onMoved(picked);
    } catch {
      setError("They couldn't be moved. Nothing changed.");
      setBusy(false);
    }
  }

  const count = itemIds.length;

  return (
    <Dialog title={count === 1 ? 'Move To Room' : `Move ${count} Items`} onClose={onClose}>
      <p className="dialog__note">
        {count === 1 ? 'It goes' : 'They go'} to the top of that room. Photos, notes and price
        come too; sections don’t travel between rooms.
      </p>

      {rooms !== undefined && rooms.length <= 1 && (
        <p className="dialog__error">There’s nowhere else to put it yet. Add another room first.</p>
      )}

      <ul className="picker">
        {(rooms ?? []).map((room) => {
          const Icon = iconByName(room.icon);
          const here = room.id === fromRoomId;

          return (
            <li key={room.id}>
              <button
                type="button"
                className={`picker__room${picked === room.id ? ' picker__room--on' : ''}`}
                disabled={here || busy}
                aria-pressed={picked === room.id}
                onClick={() => setPicked(room.id)}
              >
                <Icon size={18} aria-hidden />
                <span className="picker__name">{room.name}</span>
                {here && <span className="picker__note">already here</span>}
                {picked === room.id && <Check size={16} aria-hidden />}
              </button>
            </li>
          );
        })}
      </ul>

      {error !== undefined && (
        <p className="dialog__error" role="alert">
          {error}
        </p>
      )}

      <DialogActions>
        <Button variant="quiet" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button onClick={() => void confirm()} disabled={busy || picked === undefined}>
          Move
        </Button>
      </DialogActions>
    </Dialog>
  );
}
